import { createContext, useContext, useEffect, useState } from "react";
import { setUser as sentrySetUser, clearUser as sentryClearUser } from "@/lib/sentry";

export type UserRole =
  | "superadmin"
  | "econome"
  | "adjoint"
  | "cabinet"
  | "achats"
  | "procure"
  | "paroisses"
  | "projets"
  | "communication"
  | "seminaires"
  | "sysadmin"
  // Commission diocésaine chargée de valider le contenu doctrinal
  // (rôle backend `commission_doctrinale`). Accès à la doctrine et à rien
  // d'autre — pas d'actualités, pas de campagnes, pas de newsletter.
  | "doctrine";

export interface AuthUser {
  username: string;
  role: UserRole;
  displayName: string;
  direction: string;
  initials: string;
  color: string;
  redirect: string;
}

// Sections du menu admin-eda (cf. admin-shell adminSections[].id).
const ALL_SECTIONS = ["content", "admin", "territory", "missions"];

// La section "doctrine" est volontairement HORS de ALL_SECTIONS : côté serveur,
// /api/doctrinal-content/admin/** n'accepte que
// admin | edaci_admin | super_admin | commission_doctrinale
// (content-service, DoctrinalContentResource.WRITE_ROLES). L'ajouter à
// ALL_SECTIONS afficherait une entrée de menu à sysadmin et econome qui
// répondrait 403 — un menu ne doit jamais promettre ce que le backend refuse.
const DOCTRINE_SECTION = "doctrine";

// Sections visibles par rôle (mappé via mapRole). edaci_admin→superadmin (tout),
// edaci_editor→communication (contenu), edaci_reader→cabinet (lecture sans admin),
// commission_doctrinale→doctrine (doctrine seule).
// Exporté pour la garde de route par rôle (lib/route-access.ts).
export const ROLE_SECTIONS: Record<UserRole, string[]> = {
  superadmin:    [...ALL_SECTIONS, DOCTRINE_SECTION],   // edaci_admin / admin / super_admin
  sysadmin:      ALL_SECTIONS,
  doctrine:      [DOCTRINE_SECTION],                    // commission_doctrinale : doctrine et rien d'autre
  communication: ["content"],                           // edaci_editor : édition contenu
  cabinet:       ["content", "territory", "missions"],  // edaci_reader : lecture, pas d'admin
  econome:       ALL_SECTIONS,
  adjoint:       ["content"],
  achats:        ["content"],
  procure:       ["content"],
  paroisses:     ["content", "territory", "missions"],
  projets:       ["content", "missions"],
  seminaires:    ["content"],
};

const TOKEN_KEY = "economat_access_token";
const REFRESH_KEY = "economat_refresh_token";
const EXPIRES_KEY = "economat_token_expires";
const USER_KEY = "economat_user";

let accessToken: string | null = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;

export function getAccessToken(): string | null {
  return accessToken;
}

function persistTokens(access: string, refresh: string | null, expiresIn: number) {
  accessToken = access;
  localStorage.setItem(TOKEN_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  localStorage.setItem(EXPIRES_KEY, String(Date.now() + expiresIn * 1000));
}

function clearTokens() {
  accessToken = null;
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(EXPIRES_KEY);
  localStorage.removeItem(USER_KEY);
}

/**
 * Rôles pleinement admin (edaci_admin/admin/super_admin → superadmin ; sysadmin).
 * Utilisé par le menu (items adminOnly) et la garde de route.
 */
export function isAdminRole(role: UserRole | undefined | null): boolean {
  return role === "superadmin" || role === "sysadmin";
}

export function mapRole(roles: string[]): UserRole {
  const lower = roles.map((r) => String(r).toLowerCase());
  // Rôles EDACI (auth-service) : edaci_admin / edaci_editor / edaci_reader.
  if (lower.some((r) => ["super_admin", "superadmin", "admin", "edaci_admin"].includes(r))) return "superadmin";
  // Commission doctrinale : testé AVANT edaci_editor/edaci_reader. Un compte
  // qui cumulerait commission_doctrinale et edaci_editor est mappé sur
  // "doctrine" — le mandat doctrinal est le plus spécifique, et le cumul n'est
  // pas le montage voulu (pour donner les deux, on donne edaci_admin).
  if (lower.includes("commission_doctrinale")) return "doctrine";
  if (lower.includes("edaci_editor")) return "communication"; // édition contenu
  if (lower.includes("edaci_reader")) return "cabinet"; // lecture/supervision
  if (lower.includes("econome")) return "econome";
  if (lower.includes("sysadmin")) return "sysadmin";
  if (lower.includes("cure") || lower.includes("priest")) return "paroisses";
  return "cabinet"; // défaut prudent = lecture (pas tous droits)
}

export interface LoginCredentials {
  username?: string;
  email?: string;
  password: string;
}

/**
 * Le compte a la double authentification : la connexion attend le code de
 * l'application d'authentification (plan admin-eda du 28/09/2026, 7h).
 */
export class DoubleAuthRequise extends Error {
  constructor(public mfaSession: string) {
    super("Code de double authentification requis");
  }
}

export async function loginWithCredentials(creds: LoginCredentials): Promise<AuthUser> {
  const res = await fetch("/api/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(creds),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || `Erreur d'authentification (${res.status})`);
  }
  const data = await res.json();
  if (data.mfaRequired) throw new DoubleAuthRequise(data.mfaSession);
  return ouvrirSession(data, creds);
}

/** Seconde étape : le code à 6 chiffres (ou un code de secours). */
export async function loginWithMfaCode(mfaSession: string, code: string, creds: LoginCredentials): Promise<AuthUser> {
  const res = await fetch("/api/auth/login/mfa", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ mfaSession, code: code.replace(/\s/g, "") }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(
      res.status === 423
        ? "Trop de codes faux : réessayez dans quelques minutes."
        : res.status === 401 && data.message === "Code invalide"
          ? "Code invalide."
          : data.message || `Erreur d'authentification (${res.status})`,
    );
  }
  return ouvrirSession(data, creds);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ouvrirSession(data: any, creds: LoginCredentials): AuthUser {
  persistTokens(data.accessToken, data.refreshToken ?? null, data.expiresIn ?? 3600);

  const role = mapRole(data.roles || []);
  const displayName = `${data.firstName ?? ""} ${data.lastName ?? ""}`.trim() || data.email || data.username || "Utilisateur";
  const initials = displayName
    .split(/\s+/)
    .map((s: string) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const user: AuthUser = {
    username: data.email || data.username || creds.email || creds.username || "",
    role,
    displayName,
    direction: data.direction || "",
    initials,
    color: "bg-slate-100",
    redirect: "/dashboard",
  };
  localStorage.setItem(USER_KEY, JSON.stringify(user));

  // Sentry : associe l'utilisateur courant pour enrichir les events futurs.
  // No-op si Sentry n'est pas initialisé (DSN absent en dev).
  sentrySetUser({
    id: data.userId || data.id || user.username,
    email: data.email || user.username,
    username: user.displayName || user.username,
    role: user.role,
    direction: user.direction || undefined,
  });

  return user;
}

export function logout(): void {
  clearTokens();
  sentryClearUser();
}

/**
 * Vrai si un access token est présent ET pas expiré. Utilisé par le route guard
 * et le queryClient pour décider d'une redirection /login.
 */
export function isAuthenticated(): boolean {
  if (typeof window === "undefined") return false;
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return false;
  const expiresAt = Number(localStorage.getItem(EXPIRES_KEY) || "0");
  if (expiresAt && Date.now() > expiresAt) {
    clearTokens();
    return false;
  }
  return true;
}

interface AuthContextType {
  user: AuthUser | null;
  setUser: (user: AuthUser | null) => void;
  hasSection: (sectionId: string) => boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  setUser: () => {},
  hasSection: () => true,
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUserState] = useState<AuthUser | null>(() => {
    if (typeof window === "undefined") return null;
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  });

  const setUser = (next: AuthUser | null) => {
    setUserState(next);
    if (next) {
      localStorage.setItem(USER_KEY, JSON.stringify(next));
      // Resync Sentry sur changement d'utilisateur (login direct via context).
      sentrySetUser({
        id: next.username,
        email: next.username,
        username: next.displayName,
        role: next.role,
        direction: next.direction || undefined,
      });
    } else {
      clearTokens();
      sentryClearUser();
    }
  };

  useEffect(() => {
    const expiresAt = Number(localStorage.getItem(EXPIRES_KEY) || "0");
    if (expiresAt && Date.now() > expiresAt) {
      clearTokens();
      setUserState(null);
      sentryClearUser();
      return;
    }
    // Rehydration : si on a un user persisté en localStorage, on le repropage
    // à Sentry pour que les événements remontent avec l'identité dès le mount.
    if (user) {
      sentrySetUser({
        id: user.username,
        email: user.username,
        username: user.displayName,
        role: user.role,
        direction: user.direction || undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasSection = (sectionId: string): boolean => {
    if (!user) return true;
    return ROLE_SECTIONS[user.role]?.includes(sectionId) ?? false;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, hasSection }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
